import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query';

import { apiClient, fleetPath } from '@/lib/api-client';
import { adaptEventPage, type ApiEventPage } from '@/lib/fleet-adapters';
import type { FleetEvent, Paginated } from '@/types/fleet';

import { fleetKeys } from './fleet-keys';

const PAGE_SIZE = 25;

/** Infinite-scroll event history for a server (GET /servers/:name/events). */
export function useFleetEvents(
  name: string | undefined,
  eventType?: string,
): UseInfiniteQueryResult<InfiniteData<Paginated<FleetEvent>>, Error> {
  return useInfiniteQuery({
    queryKey: fleetKeys.events(name ?? '', eventType),
    enabled: Boolean(name),
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) =>
      adaptEventPage(
        await apiClient.get<ApiEventPage>(
          fleetPath(`/servers/${encodeURIComponent(name as string)}/events`, {
            event_type: eventType,
            page: pageParam,
            page_size: PAGE_SIZE,
          }),
          signal,
        ),
      ),
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.page_size;

      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
  });
}
